import type { JSX, FormEvent } from "react";
import { useState } from "react";
import { ContactSchema, submitContact } from "@/lib/contact";
import { StatusBadge } from "@/ui/HUD/StatusBadge";
import { usePresenceStore } from "@/state/usePresenceStore";

type SubmitState = "idle" | "sending" | "ok" | "err";

export const ContactForm = (): JSX.Element => {
  const responseHours = usePresenceStore((s) => s.responseHours);
  const [state, setState] = useState<SubmitState>("idle");
  const [errors, setErrors] = useState<string[]>([]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);
    const fd = new FormData(e.currentTarget);
    const parsed = ContactSchema.safeParse({
      name: fd.get("name"),
      email: fd.get("email"),
      brief: fd.get("brief"),
    });
    if (!parsed.success) {
      setErrors(
        parsed.error.issues.map(
          (iss) => `${iss.path.join(".") || "field"}: ${iss.message}`,
        ),
      );
      return;
    }
    setState("sending");
    try {
      await submitContact(parsed.data);
      setState("ok");
    } catch {
      setState("err");
    }
  };

  return (
    <div className="max-w-md font-sans">
      <StatusBadge />
      <form
        onSubmit={(e) => {
          void onSubmit(e);
        }}
        className="mt-6 space-y-3"
      >
        <label className="block">
          <span className="font-mono text-xs uppercase text-ink2">name</span>
          <input
            name="name"
            autoComplete="name"
            required
            className="mt-1 w-full border border-rule bg-bg1 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="font-mono text-xs uppercase text-ink2">email</span>
          <input
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            className="mt-1 w-full border border-rule bg-bg1 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="font-mono text-xs uppercase text-ink2">brief</span>
          <textarea
            name="brief"
            required
            rows={5}
            className="mt-1 w-full border border-rule bg-bg1 px-2 py-1.5 text-sm"
          />
        </label>
        {errors.length > 0 && (
          <ul className="text-xs text-danger">
            {errors.map((er, i) => (
              <li key={i}>{er}</li>
            ))}
          </ul>
        )}
        <button
          type="submit"
          disabled={state === "sending"}
          className="border border-phosphor px-4 py-1.5 font-mono text-xs uppercase tracking-wider text-phosphor disabled:opacity-50"
        >
          {state === "sending" ? "sending…" : "send"}
        </button>
        {state === "ok" && (
          <p className="text-xs text-phosphor">
            Sent. Adam usually replies within {responseHours}h.
          </p>
        )}
        {state === "err" && (
          <p className="text-xs text-danger">
            Send failed. Try the{" "}
            <a
              href="mailto:ad.kotlorz@gmail.com"
              className="underline hover:text-phosphor"
            >
              email link
            </a>
            .
          </p>
        )}
      </form>
    </div>
  );
};
